import { Box, Divider, Table as CTable, Thead, Tbody, Heading } from '@chakra-ui/react';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollSync, ScrollSyncPane } from 'react-scroll-sync';

import { EmptyTableRow } from 'components/Table';
import ReactTablePagination from 'components/Table/ReactTablePagination';
import ExportMenu from 'containers/Reports/shared/ExportMenu';
import { ReportsTableDataCell, ReportsTableHeadCell } from 'containers/Reports/shared/ReportsTableCell';
import ReportsTableRow from 'containers/Reports/shared/ReportsTableRow';
import useStickyHeader from 'containers/Reports/shared/useStickyHeader';
import { formatCurrency } from 'utils/format';
// import { useCompanyInfo, useUserInfo } from 'store/useUserStore';

const initialPremiumPayReportTableQuery = {
  current_page: 1,
  first_page_url: '',
  from: 0,
  last_page: 0,
  last_page_url: '',
  next_page_url: '',
  path: '',
  per_page: 10,
  to: 0,
  total: 0,
};

const PremiumPayReportsTable: React.FC<{
  data: any;
  dateRange: any[];
  message: string;
  children?: React.ReactNode;
}> = ({ children, data, dateRange, message }) => {
  const { t } = useTranslation(['common', 'reports', 'employees']);

  // custom pagination starts //
  const [paginationQuery, setPaginationQuery] = useState<typeof initialPremiumPayReportTableQuery>(
    initialPremiumPayReportTableQuery
  );

  const handlePageChange = useCallback((selectedPageIndex: number) => {
    const updatePaginationQuery = (previousSearchQuery: typeof paginationQuery) => ({
      ...previousSearchQuery,
      current_page: selectedPageIndex + 1,
    });
    setPaginationQuery(updatePaginationQuery);
  }, []);

  const handlePageSizeChange = (pageSize: number) => {
    setPaginationQuery((previousSearchQuery: typeof paginationQuery) => ({
      ...previousSearchQuery,
      current_page: 1,
      per_page: pageSize,
    }));
  };

  const formattedData: any[] =
    Array.isArray(data?.users) &&
    data.users
      .map((user: any) => {
        return [
          ...(Array.isArray(user?.data?.daily_premiums) && user.data?.daily_premiums.map((premium: any) => premium)),
          { ...user?.data?.total, isTotal: true },
        ];
      })
      .flat();

  const getPaginatedData = useCallback(() => {
    let firstIndex = 0;
    let secondIndex: number = formattedData.length;

    let totalCounters = 0;

    // If the next item from the last item of a page is the total then display it too
    if (formattedData[paginationQuery.current_page * +paginationQuery.per_page]?.isTotal) {
      firstIndex = (paginationQuery.current_page - 1) * +paginationQuery.per_page;
      secondIndex = paginationQuery.current_page * +paginationQuery.per_page + 1;
    } else if (formattedData[paginationQuery.current_page * +paginationQuery.per_page - 1]?.isTotal) {
      // If the first item of a page is the total then don't display it
      firstIndex = (paginationQuery.current_page - 1) * +paginationQuery.per_page;
      secondIndex = paginationQuery.current_page * +paginationQuery.per_page;
    } else {
      firstIndex = (paginationQuery.current_page - 1) * +paginationQuery.per_page;
      secondIndex = paginationQuery.current_page * +paginationQuery.per_page;
    }

    // Count how many totals are there in the interval and increment the interval accordingly to render out a correct number of items
    for (let i = 0; i < secondIndex; i++) {
      if (formattedData[i]?.isTotal) {
        totalCounters++;
      }
    }

    return formattedData.slice(firstIndex, secondIndex + totalCounters);
  }, [paginationQuery, formattedData]);

  useStickyHeader({ tableData: data, paginationQuery: paginationQuery });

  const currentItems = getPaginatedData() || [];

  // custom pagination ends //

  return (
    <>
      <Divider my="4" />
      <Heading variant="h2" size="md" fontWeight="semibold" mb="4">
        {message}
      </Heading>
      {data?.attributes && currentItems.length > 0 ? (
        <ExportMenu
          dateRange={dateRange}
          reportType="premiumPayReport"
          attributes={data?.attributes}
          displaySettings={data?.integrations}
        />
      ) : null}
      <ScrollSync>
        <Box position="relative">
          <CTable display="block" id="js-report-table">
            <ScrollSyncPane group="horizontal">
              <Thead
                display="block"
                position="sticky"
                top="0"
                background="primary.50"
                max-width="100%"
                overflow="auto"
                sx={{
                  '&::-webkit-scrollbar': { display: 'none' },
                  '-ms-overflow-style': 'none',
                  scrollbarWidth: 'none',
                }}
              >
                <ReportsTableRow color="gray.600" mb="0" border="0">
                  <ReportsTableHeadCell width="200px">{t('common:name')}</ReportsTableHeadCell>
                  <ReportsTableHeadCell width="250px">{t('common:email')}</ReportsTableHeadCell>
                  <ReportsTableHeadCell width="120px">{t('common:date')}</ReportsTableHeadCell>
                  <ReportsTableHeadCell width="120px">{t('common:duration')}</ReportsTableHeadCell>
                  {/* <ReportsTableHeadCell width="120px">{t('reports:formFilled')}</ReportsTableHeadCell> */}
                  <ReportsTableHeadCell width="120px">{t('reports:expectedMealBreaks')}</ReportsTableHeadCell>
                  <ReportsTableHeadCell width="120px">{t('reports:mealBreaksTaken')}</ReportsTableHeadCell>
                  <ReportsTableHeadCell width="120px">{t('reports:mealPremiumPay')}</ReportsTableHeadCell>
                  {/* <ReportsTableHeadCell width="120px">{t('reports:mealWaived')}</ReportsTableHeadCell> */}
                  <ReportsTableHeadCell width="120px">{t('reports:expectedRestBreaks')}</ReportsTableHeadCell>
                  <ReportsTableHeadCell width="120px">{t('reports:restBreaksTaken')}</ReportsTableHeadCell>
                  {/* <ReportsTableHeadCell width="120px">{t('reports:restWaived')}</ReportsTableHeadCell> */}
                  <ReportsTableHeadCell width="120px">{t('reports:restPremiumPay')}</ReportsTableHeadCell>
                </ReportsTableRow>
              </Thead>
            </ScrollSyncPane>
            <ScrollSyncPane group="horizontal">
              <Tbody display="block" max-width="100%" overflow="auto" mt={2}>
                {currentItems.length > 0 ? (
                  currentItems.map((item: any, index: number) => {
                    return (
                      <React.Fragment key={index}>
                        {/* Total Data */}
                        {item?.isTotal ? (
                          <ReportsTableRow bg="primary.50" minWidth="100%" width="fit-content" className="js-row-total">
                            {item?.name ? (
                              <ReportsTableDataCell fontWeight="semibold">{item?.name}</ReportsTableDataCell>
                            ) : null}
                            <ReportsTableDataCell fontWeight="semibold">
                              {t('reports:totalMealPremium')}:{' '}
                              {item?.meal_premium ? formatCurrency(+item?.meal_premium) : '0.00'}
                            </ReportsTableDataCell>
                            <ReportsTableDataCell fontWeight="semibold">
                              {t('reports:totalRestPremium')}:{' '}
                              {item?.rest_premium ? formatCurrency(+item?.rest_premium) : '0.00'}
                            </ReportsTableDataCell>
                            <ReportsTableDataCell fontWeight="semibold">
                              {t('reports:totalPremium')}:{' '}
                              {item?.total_premium ? formatCurrency(+item?.total_premium) : '0.00'}
                            </ReportsTableDataCell>
                          </ReportsTableRow>
                        ) : (
                          <>
                            {/* Timesheet Data */}
                            <ReportsTableRow
                              key={item?.id}
                              minWidth="100%"
                              width="fit-content"
                              background={
                                item.meal_premium_waived
                                  ? 'newRed.100'
                                  : item.rest_premium_waived
                                    ? 'newOrange.100'
                                    : ''
                              }
                            >
                              <ReportsTableDataCell width="200px">
                                {index === 0 || (index > 0 && currentItems[index - 1].isTotal) ? item.name : ''}
                              </ReportsTableDataCell>
                              <ReportsTableDataCell width="250px">
                                {index === 0 || (index > 0 && currentItems[index - 1].isTotal) ? item.email : ''}
                              </ReportsTableDataCell>
                              <ReportsTableDataCell width="120px">
                                {item?.date ? item?.date : '- '}
                              </ReportsTableDataCell>
                              <ReportsTableDataCell width="120px">
                                {item?.duration_in_hours || '0 '}
                              </ReportsTableDataCell>
                              <ReportsTableDataCell width="120px">
                                {item?.expected_meal_breaks || '0'}
                              </ReportsTableDataCell>
                              <ReportsTableDataCell width="120px">
                                {item?.taken_meal_breaks || '0 '}
                              </ReportsTableDataCell>
                              <ReportsTableDataCell width="120px">{item?.meal_premium || '0'}</ReportsTableDataCell>
                              <ReportsTableDataCell width="120px">
                                {item?.expected_rest_breaks || '0'}
                              </ReportsTableDataCell>
                              <ReportsTableDataCell width="120px">
                                {item?.taken_rest_breaks || '0'}
                              </ReportsTableDataCell>
                              <ReportsTableDataCell width="120px">{item?.rest_premium || '0 '}</ReportsTableDataCell>
                            </ReportsTableRow>
                          </>
                        )}
                      </React.Fragment>
                    );
                  })
                ) : (
                  <EmptyTableRow />
                )}
              </Tbody>
            </ScrollSyncPane>
          </CTable>
        </Box>
      </ScrollSync>
      <ReactTablePagination
        pageCount={data ? Math.ceil(formattedData.length / +paginationQuery.per_page) : 1}
        onPageChange={handlePageChange}
        currentPage={paginationQuery.current_page - 1}
        totalItems={formattedData.length || 0}
        rowsPerPage={(paginationQuery.per_page as number) ?? 10}
        handlePageSizeChange={handlePageSizeChange}
      />
      {children}
    </>
  );
};

export default PremiumPayReportsTable;
